import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ShieldCheck, Check, ChevronDown, Lock } from 'lucide-react';
interface PremiumCheckoutProps {
  spots: number;
  joinedCount: number;
}
export function PremiumCheckout({
  spots,
  joinedCount
}: PremiumCheckoutProps) {
  const [timeLeft, setTimeLeft] = useState(900); // 15 minutes
  const [isGuaranteeOpen, setIsGuaranteeOpen] = useState(false);
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  return <section id="purchase" className="py-20 bg-[#FAF9F6] px-4 pt-[10px] pb-[10px] space-y-[0px]" data-id="element-213">
      <div className="max-w-md mx-auto space-y-8" data-id="element-214">
        {/* Social Proof & Title */}
        <div className="text-center space-y-2" data-id="element-215">
          <p className="text-[#C2185B] text-xs font-bold tracking-widest uppercase" data-id="element-216">
            +{joinedCount.toLocaleString('es-AR')} LECTORAS
          </p>
          <h2 className="text-3xl md:text-4xl font-serif text-[#2D2D2D] leading-tight" data-id="element-217">
            Abraza a la Niña que Fuiste (PDF)
          </h2>
          <div className="flex items-center justify-center gap-1 mt-2" data-id="element-218">
            <div className="flex text-[#D4AF37]" data-id="element-219">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" data-id="element-220" />)}
            </div>
            <span className="text-sm font-medium text-[#666666] ml-1" data-id="element-221">
              (4.9)
            </span>
          </div>
        </div>

        {/* Price Block */}
        <div className="bg-white rounded-[2rem] p-8 shadow-sm border border-[#FDF2F2] text-center relative overflow-hidden" data-id="element-222">
          <div className="absolute top-4 right-4" data-id="element-223">
            <span className="bg-[#FCE4EC] text-[#C2185B] text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-224">
              50% OFF
            </span>
          </div>

          <div className="mb-6" data-id="element-225">
            <img src="https://cdn.magicpatterns.com/uploads/g9kbsi2guxjDzD5LzS1pL7/ChatGPT_Image_2_jun_2026,_16_28_17.webp" alt="Ebook Abraza a la Niña que Fuiste" className="w-full max-w-[320px] mx-auto rounded-xl shadow-md" data-id="element-226" />
          </div>

          <div className="space-y-1" data-id="element-227">
            <p className="text-[#999999] line-through text-lg font-medium" data-id="element-228">
              $40.000 ARS
            </p>
            <p className="text-4xl font-serif text-[#2D2D2D] font-bold" data-id="element-229">
              $19.990 ARS
            </p>
          </div>

          {/* Urgency Box */}
          <div className="mt-8 bg-gradient-to-br from-[#FFF5F5] to-[#FDF2F2] rounded-2xl p-6 border border-[#FCE4EC] space-y-4" data-id="element-230">
            <div className="space-y-1" data-id="element-231">
              <p className="text-[#C2185B] text-[10px] font-black tracking-[0.2em] uppercase" data-id="element-232">
                OFERTA ESPECIAL DE HOY
              </p>
              <p className="text-xs text-[#666666] leading-relaxed" data-id="element-233">
                Tu acceso quedará reservado durante los próximos 15 minutos.
              </p>
            </div>

            <div className="text-4xl font-mono font-bold text-[#2D2D2D] tracking-tighter" data-id="element-234">
              {formatTime(timeLeft)}
            </div>

            <p className="text-[10px] font-bold text-[#C2185B] animate-pulse" data-id="element-235">
              Quedan pocos accesos disponibles.
            </p>
          </div>

          {/* Main CTA */}
          <div className="mt-8" data-id="element-236">
            <button className="w-full bg-[#22c55e] text-white py-5 rounded-2xl font-black text-lg shadow-lg shadow-[#22c55e]/20 hover:bg-[#16a34a] transition-all transform hover:scale-[1.02] active:scale-[0.98] uppercase tracking-wider" data-id="element-237">
              QUIERO MI ACCESO
            </button>
          </div>

          {/* Trust Elements */}
          <div className="mt-6" data-id="element-238">
            <img src="https://cdn.magicpatterns.com/uploads/hy6veNhSXGvtYTuzySkzDu/capture_260410_163646.png" alt="Pago seguro garantizado" className="w-full h-auto" data-id="element-239" />
          </div>
        </div>

        {/* Guarantee Accordion */}
        <div className="bg-white rounded-2xl border border-[#FDF2F2] overflow-hidden" data-id="element-240">
          <button onClick={() => setIsGuaranteeOpen(!isGuaranteeOpen)} className="w-full flex items-center justify-between p-5 text-left hover:bg-[#FDF2F2]/30 transition-colors" data-id="element-241">
            <div className="flex items-center gap-3 text-[#2D2D2D] font-bold text-sm" data-id="element-242">
              <Lock size={16} className="text-[#C2185B]" data-id="element-243" />
              <span data-id="element-244">Garantía de satisfacción 14 días</span>
            </div>
            <ChevronDown size={18} className={`text-[#666666] transition-transform duration-300 ${isGuaranteeOpen ? 'rotate-180' : ''}`} data-id="element-245" />
          </button>
          <AnimatePresence data-id="element-246">
            {isGuaranteeOpen && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.3
          }} data-id="element-247">
                <div className="p-5 pt-0 text-xs text-[#666666] leading-relaxed border-t border-[#FDF2F2]" data-id="element-248">
                  Confiamos plenamente en el valor de nuestro material. Si en
                  los primeros 14 días sientes que este ebook no es para ti, te
                  devolvemos el 100% de tu inversión sin preguntas. Tu
                  crecimiento es nuestra prioridad.
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </section>;
}